import { ResumeData, ResumeFormat } from "@/types/resume";
import { formatDateFull } from "@/lib/resumeUtils";
import { SectionVisibility, defaultSectionVisibility } from "@/components/SectionVisibilityControls";
import { SectionKey, DEFAULT_SECTION_ORDER } from "@/components/DraggableSectionTabs";

interface ResumePreviewProps {
  data: ResumeData;
  format?: ResumeFormat;
  sectionVisibility?: SectionVisibility;
  sectionOrder?: SectionKey[];
}

// Helper to format links nicely
const formatLink = (url: string | undefined, label: string): { text: string; href: string } | null => {
  if (!url) return null;
  let cleanUrl = url.trim();
  const href = cleanUrl.startsWith("http") ? cleanUrl : `https://${cleanUrl}`;
  return { text: label, href };
};

export const ResumePreview = ({ data, format = 'standard', sectionVisibility = defaultSectionVisibility, sectionOrder = DEFAULT_SECTION_ORDER }: ResumePreviewProps) => {
  const hasContent = data.personalInfo.fullName || data.summary || (data.experience || []).length > 0;

  // Safe arrays
  const experience = sectionVisibility.experience ? (data.experience || []) : [];
  const education = sectionVisibility.education ? (data.education || []) : [];
  const coreStrengths = sectionVisibility.skills ? (data.coreStrengths || []) : [];
  const customSections = sectionVisibility.customSections ? (data.customSections || []) : [];
  const skills = sectionVisibility.skills ? (data.skills || []) : [];
  const summary = sectionVisibility.summary ? data.summary : "";

  const isCompact = format === 'compact';

  if (!hasContent) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <div className="w-16 h-20 mx-auto mb-4 border-2 border-dashed border-muted-foreground/30 rounded-lg flex items-center justify-center">
            <span className="text-2xl text-muted-foreground/50">📄</span>
          </div>
          <p className="text-sm">Start filling in your details</p>
          <p className="text-xs mt-1">Your resume preview will appear here</p>
        </div>
      </div>
    );
  }

  // Build contact parts with clickable links
  const contactElements: React.ReactNode[] = [];
  
  if (data.personalInfo.location) contactElements.push(data.personalInfo.location);
  if (data.personalInfo.phone) contactElements.push(data.personalInfo.phone);
  if (data.personalInfo.email) {
    contactElements.push(
      <a key="email" href={`mailto:${data.personalInfo.email}`} className="text-[#0000EE] underline">
        {data.personalInfo.email}
      </a>
    );
  }

  const githubLink = formatLink(data.personalInfo.github, "GitHub");
  if (githubLink) {
    contactElements.push(
      <a key="github" href={githubLink.href} target="_blank" rel="noopener noreferrer" className="text-[#0000EE] underline">
        GitHub
      </a>
    );
  }

  const portfolioLink = formatLink(data.personalInfo.portfolio, "Portfolio");
  if (portfolioLink) {
    contactElements.push(
      <a key="portfolio" href={portfolioLink.href} target="_blank" rel="noopener noreferrer" className="text-[#0000EE] underline">
        Portfolio
      </a>
    );
  }
  
  const linkedinLink = formatLink(data.personalInfo.linkedin, "LinkedIn");
  if (linkedinLink) {
    contactElements.push(
      <a key="linkedin" href={linkedinLink.href} target="_blank" rel="noopener noreferrer" className="text-[#0000EE] underline">
        LinkedIn
      </a>
    );
  }

  const sectionSpacingPt = isCompact ? 1 : 2;

  // Section renderers
  const renderEducation = () => {
    if (education.length === 0) return null;
    return (
      <section key="education" style={{ marginBottom: `${sectionSpacingPt}pt` }}>
        <SectionHeader title="EDUCATION" />
        {education.map((edu) => {
          const gradDate = formatDateFull(edu.graduationDate);
          return (
            <div key={edu.id} style={{ marginTop: '3pt' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontSize: '11pt', fontWeight: 'bold' }}>{edu.institution}</span>
                {edu.location && <span style={{ fontSize: '10pt', fontWeight: 'bold' }}>{edu.location}</span>}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: '10pt', lineHeight: '12pt' }}>
                <span>{edu.degree}{edu.field && ` in ${edu.field}`}{edu.gpa && ` | GPA: ${edu.gpa}`}</span>
                {gradDate && <span style={{ fontWeight: 'bold' }}>{gradDate}</span>}
              </div>
            </div>
          );
        })}
      </section>
    );
  };

  const renderSummary = () => {
    if (!summary) return null;
    return (
      <section key="summary" style={{ marginBottom: `${sectionSpacingPt}pt` }}>
        <SectionHeader title="SUMMARY" />
        <p style={{ 
          fontSize: '10pt', 
          lineHeight: '12pt', 
          textAlign: 'justify',
          margin: 0,
          marginTop: '3pt',
        }}>
          {summary}
        </p>
      </section>
    );
  };

  const renderSkills = () => {
    if (coreStrengths.length > 0 && coreStrengths.some(c => c.category && c.skills)) {
      return (
        <section key="skills-core" style={{ marginBottom: `${sectionSpacingPt}pt` }}>
          <SectionHeader title="SKILLS" />
          <div style={{ fontSize: '10pt', lineHeight: '14pt', marginTop: '3pt' }}>
            {coreStrengths.filter(c => c.category && c.skills).map((cat) => (
              <div key={cat.id} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '2pt' }}>
                <span style={{ marginRight: '6px', flexShrink: 0 }}>•</span>
                <span>
                  <span style={{ fontWeight: 'bold' }}>{cat.category}:</span> {cat.skills}
                </span>
              </div>
            ))}
          </div>
        </section>
      );
    }
    if (skills.length > 0 && skills.some(Boolean)) {
      return (
        <section key="skills-legacy" style={{ marginBottom: `${sectionSpacingPt}pt` }}>
          <SectionHeader title="SKILLS" />
          <p style={{ fontSize: '10pt', lineHeight: '12pt', marginTop: '3pt', margin: 0 }}>
            {skills.filter(Boolean).join(" • ")}
          </p>
        </section>
      );
    }
    return null;
  };

  const renderExperience = () => {
    if (experience.length === 0) return null;
    return (
      <section key="experience" style={{ marginBottom: `${sectionSpacingPt}pt` }}>
        <SectionHeader title="EXPERIENCE" />
        {experience.map((exp) => {
          const startFormatted = formatDateFull(exp.startDate);
          const endFormatted = exp.current ? "Present" : formatDateFull(exp.endDate);
          const hasDateRange = startFormatted || endFormatted;
          const dateDisplay = hasDateRange 
            ? `${startFormatted || ""}${startFormatted && endFormatted ? " - " : ""}${endFormatted || ""}`
            : "";
          const locationParts = [exp.location, exp.workType].filter(Boolean);
          const locationDisplay = locationParts.join(" | ");
          
          return (
            <div key={exp.id} style={{ marginTop: '3pt' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontSize: '11pt', fontWeight: 'bold' }}>{exp.company}</span>
                {dateDisplay && <span style={{ fontSize: '10pt', fontWeight: 'bold' }}>{dateDisplay}</span>}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontSize: '10pt', fontWeight: 'bold' }}>{exp.jobTitle}</span>
                {locationDisplay && <span style={{ fontSize: '10pt', fontWeight: 'bold' }}>{locationDisplay}</span>}
              </div>
              {(exp.bullets || []).filter(Boolean).length > 0 && (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0, marginTop: '1pt', fontSize: '10pt', lineHeight: '12pt' }}>
                  {(exp.bullets || []).filter(Boolean).map((bullet, idx) => (
                    <li key={idx} style={{ paddingLeft: '12px', position: 'relative', textAlign: 'justify' }}>
                      <span style={{ position: 'absolute', left: 0 }}>•</span>
                      {bullet}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </section>
    );
  };

  const renderCustomItem = (item: any, isProject: boolean, sectionTitle?: string) => {
    if (item.type === 'bullet') {
      return (
        <div key={item.id} style={{ marginTop: '1pt', fontSize: '10pt', lineHeight: '12pt', paddingLeft: '12px', position: 'relative' }}>
          <span style={{ position: 'absolute', left: 0 }}>•</span>
          {item.title}
        </div>
      );
    }
    const isTitleRedundant = (t: string, s: string) => {
      const a = t.toLowerCase().trim();
      const b = s.toLowerCase().trim();
      return a === b || a.startsWith(b) || b.startsWith(a);
    };
    const showTitle = item.title && (!sectionTitle || !isTitleRedundant(item.title, sectionTitle));
    return (
      <div key={item.id} style={{ marginTop: isProject ? '3pt' : '2pt' }}>
        {isProject ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontSize: '11pt', fontWeight: 'bold' }}>
                {item.title}
                {item.link && (
                  <>
                    <span style={{ fontWeight: 'normal', margin: '0 6px' }}>|</span>
                    <a href={item.link.startsWith('http') ? item.link : `https://${item.link}`} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 'normal', color: '#0000EE', textDecoration: 'underline' }}>
                      Project Link
                    </a>
                  </>
                )}
              </span>
              {item.date && <span style={{ fontSize: '10pt' }}>{item.date}</span>}
            </div>
            {item.subtitle && <div style={{ fontSize: '10pt', lineHeight: '12pt', fontStyle: 'italic' }}>{item.subtitle}</div>}
          </>
        ) : (
          <>
            {showTitle && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontSize: '10pt', fontWeight: 'normal' }}>{item.title}</span>
                {item.date && <span style={{ fontSize: '10pt', fontWeight: 'normal' }}>{item.date}</span>}
              </div>
            )}
            {item.subtitle && <div style={{ fontSize: '10pt', lineHeight: '12pt', fontWeight: 'normal' }}>{item.subtitle}</div>}
            {item.link && (
              <div style={{ fontSize: '10pt', lineHeight: '12pt', fontWeight: 'normal' }}>
                <a href={item.link.startsWith('http') ? item.link : `https://${item.link}`} target="_blank" rel="noopener noreferrer" className="text-[#0000EE] underline">
                  {/project/i.test(item.title || "") ? "Project Link" : "View Link"}
                </a>
              </div>
            )}
          </>
        )}
        {item.description && <div style={{ fontSize: '10pt', lineHeight: '12pt', fontWeight: 'normal' }}>{item.description}</div>}
        {(item.bullets || []).filter(Boolean).length > 0 && (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, marginTop: '1pt', fontSize: '10pt', lineHeight: '12pt', fontWeight: 'normal' }}>
            {(item.bullets || []).filter(Boolean).map((bullet: string, idx: number) => (
              <li key={idx} style={{ paddingLeft: '12px', position: 'relative', textAlign: 'justify' }}>
                <span style={{ position: 'absolute', left: 0 }}>•</span>
                {bullet}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const renderCustomSections = () => {
    if (customSections.length === 0) return null;
    const certSections = customSections.filter(s => /certif|licen/i.test(s.title || ""));
    const projSections = customSections.filter(s => /project/i.test(s.title || ""));
    const otherSections = customSections.filter(s => s.title && !/certif|licen|project/i.test(s.title));

    return (
      <>
        {certSections.map((section) => (
          <section key={section.id} style={{ marginBottom: `${sectionSpacingPt}pt` }}>
            <SectionHeader title={section.title.toUpperCase()} />
            {(section.items || []).map((item) => renderCustomItem(item, false, section.title))}
          </section>
        ))}
        {projSections.map((section) => (
          <section key={section.id} style={{ marginBottom: `${sectionSpacingPt}pt` }}>
            <SectionHeader title={section.title.toUpperCase()} />
            {(section.items || []).map((item) => renderCustomItem(item, true))}
          </section>
        ))}
        {otherSections.map((section) => (
          <section key={section.id} style={{ marginBottom: `${sectionSpacingPt}pt` }}>
            <SectionHeader title={section.title.toUpperCase()} />
            {(section.items || []).map((item) => renderCustomItem(item, false, section.title))}
          </section>
        ))}
      </>
    );
  };

  // Map section keys to renderers
  const sectionRenderers: Record<string, () => React.ReactNode> = {
    education: renderEducation,
    summary: renderSummary,
    skills: renderSkills,
    experience: renderExperience,
    custom: renderCustomSections,
  };

  // Render sections in user-defined order (skip "personal" as header is always first)
  const orderedSections = sectionOrder
    .filter(key => key !== "personal")
    .map(key => sectionRenderers[key]?.())
    .filter(Boolean);

  return (
    <div 
      className="bg-white text-black shadow-lg"
      style={{
        width: '8.5in',
        minHeight: '11in',
        paddingTop: '0.4in',
        paddingBottom: '0.4in',
        paddingLeft: '0.55in',
        paddingRight: '0.55in',
        fontFamily: "'Times New Roman', Times, serif",
        fontSize: '10pt',
        lineHeight: '12pt',
        boxSizing: 'border-box',
      }}
    >
      {/* Header - Name centered */}
      <div style={{ textAlign: 'center', marginBottom: `${sectionSpacingPt}pt` }}>
        <h1 style={{ 
          fontSize: isCompact ? '18pt' : '21pt', 
          fontWeight: 'bold', 
          letterSpacing: '0.05em',
          margin: 0,
          lineHeight: '1.2',
        }}>
          {data.personalInfo.fullName.toUpperCase() || "YOUR NAME"}
        </h1>
        <div style={{ fontSize: '10pt', lineHeight: '12pt', marginTop: '2pt' }}>
          {contactElements.map((el, idx) => (
            <span key={idx}>
              {idx > 0 && <span style={{ margin: '0 4px' }}>|</span>}
              {el}
            </span>
          ))}
        </div>
      </div>

      {/* Sections in user-defined order */}
      {orderedSections}
    </div>
  );
};

// Section header component with full-width black underline
const SectionHeader = ({ title }: { title: string }) => (
  <div style={{ marginBottom: '0' }}>
    <h2 style={{ 
      fontSize: '12pt', 
      fontWeight: 'bold', 
      margin: 0,
      paddingBottom: '3pt',
      borderBottom: '1px solid black',
      lineHeight: '1',
    }}>
      {title}
    </h2>
  </div>
);
